import {
  Box,
  ClickAwayListener,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  MessageStatus,
  MessageType,
  getMessageTimestamp,
} from "../../../interfaces/all";
import { formatMessageTime } from "../../../utils/date";
import { MessageReadStatus } from "../MessageReadStatus/MessageReadStatus";
import StatusIcon from "./StatusIcon";

interface MessageMetaInfoProps {
  message: MessageType;
  showDate?: boolean;
  isFromClient?: boolean;
  sx?: any;
  readStatus?: any; // Status de leitura atualizado
}

const MessageMetaInfo: React.FC<MessageMetaInfoProps> = React.memo(
  ({ message, isFromClient = false, sx, readStatus }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [forceUpdate, setForceUpdate] = useState(0);
    const messageTimestamp = getMessageTimestamp(message);
    // Calcular o status mais recente considerando todos os statuses
    const lastMetaStatus = React.useMemo(() => {
      const statuses = message.statuses || [];
      if (statuses.length === 0) return null;

      // Ordenar por timestamp e pegar o mais recente
      const sortedStatuses = [...statuses].sort((a, b) => {
        const timestampA = new Date(a.timestamp).getTime();
        const timestampB = new Date(b.timestamp).getTime();
        return timestampB - timestampA; // Ordem decrescente
      });

      const latestStatus = sortedStatuses[0];

      return latestStatus;
    }, [
      message.statuses,
      message._id,
      message.statuses?.length,
      message.statuses?.map((s) => `${s.status}-${s.timestamp}`).join("|"),
    ]);

    // Usar o status de leitura passado como prop ou o padrão da mensagem
    const currentReadStatus = readStatus ||
      message.readStatus || { isRead: false };

    // Forçar re-render quando o status mudar (consolidado em um único useEffect)
    useEffect(() => {
      // Só forçar re-render se o status realmente mudou
      const messageStatus = message.readStatus || { isRead: false };

      if (
        currentReadStatus?.isRead !== messageStatus.isRead ||
        currentReadStatus?.readAt !== messageStatus.readAt
      ) {
        setForceUpdate((prev) => prev + 1);
      }
    }, [
      currentReadStatus?.isRead,
      currentReadStatus?.readAt,
      message.readStatus?.isRead,
      message.readStatus?.readAt,
      message.readStatus,
    ]);

    // Forçar re-render quando o status do WhatsApp mudar
    useEffect(() => {
      setForceUpdate((prev) => prev + 1);
    }, [lastMetaStatus?.status, lastMetaStatus?.timestamp]);

    const getMetaStatusText = React.useCallback((status: MessageStatus) => {
      switch (status) {
        case "sent":
          return "Enviado";
        case "delivered":
          return "Entregue";
        case "read":
          return "Lido";
        case "failed":
          return "Falha no envio";
        default:
          return status;
      }
    }, []);

    const tooltipTitle = React.useMemo(
      () =>
        `${formatMessageTime(messageTimestamp, true)}${lastMetaStatus?.status ? ` • ${getMetaStatusText(lastMetaStatus.status)}` : ""}`,
      [messageTimestamp, lastMetaStatus?.status, getMetaStatusText],
    );

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (!isFromClient && message.statuses && message.statuses.length > 0) {
          setAnchorEl(anchorEl ? null : event.currentTarget);
        }
      },
      [isFromClient, message.statuses, anchorEl],
    );

    const handleClickAway = React.useCallback(() => {
      setAnchorEl(null);
    }, []);

    const open = Boolean(anchorEl);

    const renderMetaStatusHistory = React.useCallback(() => {
      const statuses = message.statuses || [];
      const hasReadStatus = statuses.some((s) => s.status === "read");
      const hasDeliveredStatus = statuses.some((s) => s.status === "delivered");

      const uniqueMetaStatuses = statuses.reduce(
        (acc, current) => {
          const existingIndex = acc.findIndex(
            (s) => s.status === current.status,
          );
          if (existingIndex === -1) {
            acc.push(current);
          } else {
            const existingTimestamp = new Date(
              acc[existingIndex].timestamp,
            ).getTime();
            const currentTimestamp = new Date(current.timestamp).getTime();
            if (currentTimestamp > existingTimestamp) {
              acc[existingIndex] = current;
            }
          }
          return acc;
        },
        [] as typeof statuses,
      );

      const allMetaStatuses = [...uniqueMetaStatuses];
      if (!hasReadStatus && !hasDeliveredStatus) {
        allMetaStatuses.push({
          status: "delivered",
          timestamp: new Date(message.timestamp),
        });
      }

      if (allMetaStatuses.length === 0) return null;

      const metaStatusRead = allMetaStatuses.find(
        (status) => status.status === "read",
      );

      const getMetaStatusPriority = (status: MessageStatus): number => {
        switch (status) {
          case "read":
            return 3;
          case "delivered":
            return 2;
          case "sent":
          case "failed":
            return 1;
          default:
            return 0;
        }
      };

      const metaSortedStatuses = [...allMetaStatuses].sort((a, b) => {
        const priorityA = getMetaStatusPriority(a.status);
        const priorityB = getMetaStatusPriority(b.status);

        if (priorityA !== priorityB) {
          return priorityB - priorityA;
        }

        const timestampA = new Date(a.timestamp).getTime();
        const timestampB = new Date(b.timestamp).getTime();
        return timestampB - timestampA;
      });

      return (
        <Box sx={{ p: 1, maxWidth: 300 }}>
          {!metaStatusRead && (
            <Box
              key="read"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
              }}
            >
              <StatusIcon status="read" />
              <Typography variant="body2" sx={{ color: "#667781" }}>
                Lida
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#8696a0", ml: "auto" }}
              >
                Não foi lida
              </Typography>
            </Box>
          )}

          {metaSortedStatuses.map((status, index) => (
            <Box
              key={`meta-status-${status.status}-${new Date(status.timestamp).getTime()}-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: index < metaSortedStatuses.length - 1 ? 1 : 0,
              }}
            >
              <StatusIcon status={status.status} />
              <Typography variant="body2" sx={{ color: "#667781" }}>
                {getMetaStatusText(status.status)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#8696a0", ml: "auto" }}
              >
                {formatMessageTime(new Date(status.timestamp), true)}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }, [message.statuses, message.timestamp, getMetaStatusText]);

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "0 2px",
          minWidth: "52px",
          justifyContent: "flex-end",
          position: "absolute",
          right: "0",
          bottom: "2px",
          height: "18px",
          zIndex: 1,
          pointerEvents: "none",
          "& > *": {
            pointerEvents: "auto",
          },
          ...sx,
        }}
      >
        <Tooltip
          title={tooltipTitle}
          placement="top"
          arrow
          PopperProps={{
            sx: {
              "& .MuiTooltip-tooltip": {
                backgroundColor: "#111b21",
                color: "#e9edef",
                fontSize: "12px",
                padding: "8px 12px",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
              "& .MuiTooltip-arrow": {
                color: "#111b21",
              },
            },
          }}
        >
          <Box
            key={`meta-info-${forceUpdate}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "2px",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
            }}
            onClick={handleClick}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#8696a0",
                fontSize: "11px",
                lineHeight: "1",
                userSelect: "none",
              }}
            >
              {formatMessageTime(messageTimestamp)}
            </Typography>
            {isFromClient && (
              <>
                {/* Para mensagens do cliente, mostrar apenas o status de leitura */}
                {currentReadStatus && (
                  <MessageReadStatus
                    status={currentReadStatus}
                    showTooltip={true}
                  />
                )}
              </>
            )}
            {!isFromClient && (
              <>
                {/* Para mensagens do atendente, mostrar apenas o status do WhatsApp */}
                <StatusIcon
                  status={lastMetaStatus?.status || "sent"}
                  key={`status-${message._id}-${lastMetaStatus?.status || "sent"}-${forceUpdate}`}
                />
              </>
            )}
          </Box>
        </Tooltip>

        {!isFromClient && message.statuses && message.statuses.length > 0 && (
          <ClickAwayListener onClickAway={handleClickAway}>
            <Popper
              open={open}
              anchorEl={anchorEl}
              placement="top"
              sx={{
                zIndex: 1300,
                "& .MuiPaper-root": {
                  backgroundColor: "#111b21",
                  color: "#e9edef",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  border: "1px solid #2a3942",
                },
              }}
            >
              {renderMetaStatusHistory()}
            </Popper>
          </ClickAwayListener>
        )}
      </Box>
    );
  },
);

MessageMetaInfo.displayName = "MessageMetaInfo";

export default MessageMetaInfo;
